import { Inject, Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { catchError, concatMap, exhaustMap, map, switchMap } from 'rxjs/operators'
import { BehaviorSubject, of } from 'rxjs'
import { Store } from '@ngrx/store'
import { AccountService } from './account.service'
import { ActionFailed } from '../utility'
import {
  setAccountTaxes,
  setAccountTaxexComplete,
  loadAccount,
  loadAccountComplete,
  updateAccount,
  updateAccountBillingAddresses,
  updateAccountBillingAddressesComplete,
  updateAccountComplete,
  updateDomain,
  toggleAccountMFA,
  toggleAccountMFAComplete,
  updateAccountAttributes,
  RequestAccountDataDelete,
} from '../account/account.actions'
import { CURRENT_ACCOUNT_ID } from '../../tokens'

@Injectable()
export class AccountEffects {
  loadAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAccount),
      exhaustMap(() =>
        this.account.get().pipe(
          map((account) => loadAccountComplete({ account })),
          catchError((error) => of(ActionFailed({ error })))
        )
      )
    )
  )

  updateAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateAccount),
      concatMap(({ account, updateAll }) =>
        this.account.update({ account, updateAll }).pipe(
          map((account) => updateAccountComplete({ update: { id: account.id, changes: account } })),
          catchError((error) => of(ActionFailed({ error })))
        )
      )
    )
  )

  updateAccountAttributes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateAccountAttributes),
      switchMap(({ attributes }) =>
        this.account.updateAttributes(attributes).pipe(
          map((account) => updateAccountComplete({ update: { id: account.id, changes: account } })),
          catchError((error) => of(ActionFailed({ error })))
        )
      )
    )
  )

  requestAccountDataDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RequestAccountDataDelete),
      switchMap(({ confirm, cancel }) =>
        this.account.requestDataDelete(confirm, cancel).pipe(
          map(() =>
            updateAccountComplete({
              update: { id: this.accountId$.value, changes: { request_data_deletion: confirm ? 1 : 0 } },
            })
          ),
          catchError((error) => of(ActionFailed({ error })))
        )
      )
    )
  )

  updateMFA$ = createEffect(() =>
    this.actions$.pipe(
      ofType(toggleAccountMFA),
      concatMap(({ enable_2fa }) =>
        this.account.updateMFA(enable_2fa).pipe(
          map((account) =>
            toggleAccountMFAComplete({ update: { id: this.accountId$.value, changes: { enable_2fa } } })
          ),
          catchError((error) => of(ActionFailed({ error })))
        )
      )
    )
  )

  addAccountTax$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setAccountTaxes),
      concatMap(({ taxes }) =>
        this.account.updateTaxes({ taxes }).pipe(
          map((account) => setAccountTaxexComplete({ update: { id: account.id, changes: account } })),
          catchError((error) => of(ActionFailed({ error })))
        )
      )
    )
  )

  setAccountBillingAddresses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateAccountBillingAddresses),
      concatMap(({ addresses }) =>
        this.account.updateBillingAddress(addresses).pipe(
          map((account) => updateAccountBillingAddressesComplete({ update: { id: account.id, changes: account } })),
          catchError((error) => of(ActionFailed({ error })))
        )
      )
    )
  )

  updateDomain$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateDomain),
      concatMap(({ domain, noConfirm }) => (noConfirm ? of(domain) : this.account.confirmDomainChange(domain))),
      concatMap((domain) => this.account.checkDomainLocal(domain)),
      concatMap((domain) => this.account.checkDomainRemote(domain)),
      concatMap((domain) =>
        this.account.updateDomain(domain).pipe(
          map((account) => updateAccountComplete({ update: { id: account.id, changes: account } })),
          catchError((error) => of(ActionFailed({ error })))
        )
      )
    )
  )

  constructor(
    private actions$: Actions,
    private store: Store<any>,
    private account: AccountService,
    @Inject(CURRENT_ACCOUNT_ID) private accountId$: BehaviorSubject<number>
  ) {}
}
